import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { quizQuestions } from 'src/app/models/getQuizQuestions.model';
import { QuizzesResultsByQuizzesService } from 'src/app/services/api-lab4u/quizzes/quizzes-results-by-quizzes.service';
import {
  AggregateQuestion,
  Option,
} from '../../models/AggregateQuestion.model';
import { Subscription } from 'rxjs';
import { SelectComponent } from '../select/select.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BarChartModule } from '@swimlane/ngx-charts';
import {
  TranslocoDirective,
  TranslocoPipe,
  TranslocoService,
} from '@jsverse/transloco';
import { TimeDisplayComponent } from '../quiz-results-charts/time-display/time-display.component';
import { GeneralChartComponent } from '../quiz-results-charts/general-chart/general-chart.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-quiz-questions',
  templateUrl: './quiz-questions.component.html',
  styleUrls: ['./quiz-questions.component.css'],
  imports: [
    SelectComponent,
    MatProgressSpinner,
    BarChartModule,
    TranslocoDirective,
    TimeDisplayComponent,
    GeneralChartComponent,
    TranslocoPipe,
  ],
})
export class QuizQuestionsComponent implements AfterViewInit, OnChanges {
  private subscription: Subscription = new Subscription();
  selectedQuizId!: string;
  selectedOptionId: any;
  questions?: quizQuestions[];
  AggregateQuestion?: AggregateQuestion;
  questionOption?: { name: string; value: number }[];
  resultPercentage?: number;
  filteredAndTransformedData: any[] = [];
  timeInMinutesAndSeconds?: number;
  result: any;
  statusDetail: 'loading' | 'success' | 'error' | 'init' = 'init';
  view: number[] = [840, 335];
  barNumber = 0;
  @Output() selectedQuestionChanged = new EventEmitter<string>();
  @Input() chartTitle = '';
  @Input() requestBodyQuestion: any;
  @Input() quizId = '';
  @Input() isFilterOpen = false;
  aggregatedMetricsforQuestion: any;
  private readonly quizzesMetricsKnownStatusCodes = [404, 491, 493];
  private readonly quizzesQuestionsKnownStatusCodes = [400, 404, 493];
  errorMessage: any;
  showChart = true;

  constructor(
    private quizzesResultsByQuizzesService: QuizzesResultsByQuizzesService,
    private cdr: ChangeDetectorRef,
    private transloco: TranslocoService,
  ) {}

  ngAfterViewInit(): void {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.quizId) this.getQuestion();
    if (changes.requestBodyQuestion) {
      if (
        this.requestBodyQuestion.quiz_id &&
        this.requestBodyQuestion.question_id
      )
        this.getMetricsforQuestion();
    }
    if (changes.isFilterOpen) this.toggleFilter();
  }

  toggleFilter(): void {
    this.showChart = false;

    setTimeout(() => {
      this.showChart = true;
      this.cdr.markForCheck();

      window.dispatchEvent(new Event('resize'));
    }, 300);
  }
  /**
   * Sets the selected question ID and updates the average time.
   *
   * This function emits the selected question ID using the selectedQuestionChanged event,
   * allowing other components to react to the selected question change.
   * It also updates the timeInMinutesAndSeconds property with the average time from the AggregateQuestion data.
   *
   * @param {string} id - The ID of the selected question.
   */
  handleQuestionSelected(id: string) {
    this.selectedOptionId = id;
    this.selectedQuestionChanged.emit(id);
    this.timeInMinutesAndSeconds = this.AggregateQuestion?.average_time;
  }
  /**
   * Retrieves aggregated metrics for a specific question and updates component properties.
   *
   * This function initiates a loading state, makes an HTTP request to fetch aggregated metrics for a question,
   * and updates various component properties with the retrieved metrics data.
   * It sets the statusDetail property to 'success' upon successful retrieval.
   *Set the statusDetail to 'error' and store the error message upon HTTP request failure.
   */

  getMetricsforQuestion() {
    this.statusDetail = 'loading';
    this.subscription.add(
      this.quizzesResultsByQuizzesService
        .aggregatedMetricsforQuestion(this.requestBodyQuestion)
        .subscribe(
          (res: any) => {
            const aggregateQuestion: AggregateQuestion = res.response;
            this.AggregateQuestion = aggregateQuestion;
            this.timeInMinutesAndSeconds = aggregateQuestion.average_time;
            const resultPercentage: number = res.response.general_approval;
            this.resultPercentage = resultPercentage;
            this.result = this.showSuccessResults(resultPercentage);
            const questionOption = this.showSuccessResultsOption(
              res.response.question_option_distribution,
            );
            this.questionOption = questionOption;
            this.barNumber = questionOption.reduce(
              (sum, option) => sum + option.value,
              0,
            );
            this.statusDetail = 'success';
            this.cdr.markForCheck();
          },
          (error) => {
            this.statusDetail = 'error';
            const key = this.quizzesMetricsKnownStatusCodes.includes(
              error.status,
            )
              ? `pages.QUIZZES-RESULTS.QUIZZES-METRICS-ERROR-MESSAGES.${error.status}`
              : 'pages.QUIZZES-RESULTS.QUIZZES-METRICS-ERROR-MESSAGES.DEFAULT';

            this.errorMessage = this.transloco.translate(key, {
              status: error.status,
              message: error.message,
            });
            this.cdr.markForCheck();
          },
        ),
    );
  }
  /**
   * Formats the X-axis value for display.
   *
   * This function checks if the input value is a whole number (integer). If it's a whole number, it returns the value as is; otherwise, it returns an empty string.
   *
   * @param {any} val - The value to format.
   * @returns {string} The formatted value (either the input value as a string or an empty string).
   */
  xAxisFormat(val: any) {
    if (val % 1 > 0) return '';
    return val;
  }
  /**
   * Calculates and returns the success and error percentages based on general approval.
   *
   * @param {number} generalApproval - The general approval percentage.
   * @returns {any[]} An array containing success and error percentages.
   */
  showSuccessResults(generalAproval: number) {
    const totalScorePercentage = 100;
    const resultPercentage: any[] = [
      {
        name: 'sucessText',
        value: generalAproval,
      },
      {
        name: 'errorText',
        value: totalScorePercentage - generalAproval,
      },
    ];
    return resultPercentage;
  }
  /**
   * Calculates and returns success percentages based on question option distribution.
   *
   * @param {any} option - The question option distribution data.
   * @returns {any[]} An array containing option names and their respective success percentages.
   */
  showSuccessResultsOption(option: Option[]) {
    const resultPercentage = option.map((item) => ({
      name: item.statement,
      value: item.answers,
    }));
    return resultPercentage;
  }
  /**
   * Retrieves the questions for a quiz and sets the selected question.
   *
   * This function initiates a loading state, makes an HTTP request to fetch questions for a quiz,
   * and sets the selected question based on the retrieved data.
   * It also emits the selected question using the selectedQuestionChanged event.
   * Set the statusDetail to 'error' and store the error message upon HTTP request failure.
   */
  getQuestion() {
    this.statusDetail = 'loading';
    this.quizzesResultsByQuizzesService.getQuestions(this.quizId).subscribe(
      (res: any) => {
        this.questions = res.response.questions;
        // this.handleQuestionSelected(this.questions[0].id);
        this.statusDetail = 'success';
        this.cdr.markForCheck();
      },
      (error: HttpErrorResponse) => {
        this.statusDetail = 'error';
        const key = this.quizzesQuestionsKnownStatusCodes.includes(
          error.status,
        )
          ? `pages.QUIZZES-RESULTS.QUIZZES-QUESTIONS-ERROR-MESSAGES.${error.status}`
          : 'pages.QUIZZES-RESULTS.QUIZZES-QUESTIONS-ERROR-MESSAGES.DEFAULT';

        this.errorMessage = this.transloco.translate(key, {
          status: error.status,
          message: error.message,
        });
        this.cdr.markForCheck();
      },
    );
  }

  colorSchemeMetrics = {
    domain: ['#5072ff', '#bdbdbd'],
  };

  colorScheme = {
    domain: ['#5072ff'],
  };
  /**
   * Formats time in seconds into a human-readable time string (e.g., "5:30 min.").
   *
   * @param {number} seconds - The duration in seconds to format.
   * @returns {string} The formatted time string with "min." suffix.
   */
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds < 10) {
      return `${minutes}:0${remainingSeconds}`;
    }
    return `${minutes}:${remainingSeconds}`;
  }
}
